import { ApiErrorBody, StepUpRequiredDetails, type ErrorCode } from "@townsy/contracts";
import type { ZodType } from "zod";
import { observeServerDate } from "../lib/serverClock";
import { requestStepUp } from "./stepUp";

/**
 * Same-origin JSON client for the Townsy API. Every request carries the guest/session cookies
 * (`credentials: "include"`); every failure is normalised to an {@link ApiError} carrying the
 * stable `code` from the `ApiErrorBody` contract so the UI can branch on codes, never on text.
 */
export const API_BASE = "/api/v1";

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string | undefined;
  readonly retryable: boolean;
  readonly details: unknown;

  constructor(
    code: ErrorCode,
    message: string,
    opts: { status?: number; requestId?: string; retryable?: boolean; details?: unknown } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = opts.status ?? 0;
    this.requestId = opts.requestId;
    this.retryable = opts.retryable ?? false;
    this.details = opts.details;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

/** Human-readable message for any thrown value; prefers the server's safe domain explanation. */
export function errorMessage(e: unknown, fallback = "Something went wrong."): string {
  if (isApiError(e)) return e.message || fallback;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export function isRetryableError(e: unknown): boolean {
  return isApiError(e) ? e.retryable : true;
}

export interface ApiRequest<T> {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Contract schema used to validate the response (zod strips unknown keys). */
  schema?: ZodType<T>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Allow the request to outlive the page (analytics flush on pagehide). */
  keepalive?: boolean;
  /** Browser HTTP cache mode (e.g. "no-cache" to revalidate a response the server allows to be cached). */
  cache?: RequestCache;
  /**
   * ADR-0039: on STEP_UP_REQUIRED, ask for the staff confirmation code and retry once (default true). The step-up
   * request itself and the retry pass false.
   */
  stepUp?: boolean;
}

const CODE_BY_STATUS: Record<number, ErrorCode> = {
  401: "UNAUTHENTICATED",
  403: "FORBIDDEN",
  404: "NOT_FOUND",
  409: "IDEMPOTENCY_CONFLICT",
  422: "VALIDATION_FAILED",
  429: "RATE_LIMITED",
  502: "DEPENDENCY_UNAVAILABLE",
  503: "DEPENDENCY_UNAVAILABLE",
  504: "DEPENDENCY_UNAVAILABLE",
};

async function readErrorBody(res: Response): Promise<ApiError> {
  const status = res.status;
  const requestId = res.headers.get("x-request-id") ?? undefined;
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* non-JSON error page */
  }
  const parsed = ApiErrorBody.safeParse(json);
  if (parsed.success) {
    const e = parsed.data.error;
    return new ApiError(e.code, e.message, {
      status,
      requestId: e.requestId ?? requestId,
      retryable: e.retryable,
      details: e.details,
    });
  }
  const code = CODE_BY_STATUS[status] ?? (status >= 500 ? "INTERNAL" : "VALIDATION_FAILED");
  const retryable = status === 429 || status >= 502;
  const message =
    status === 429
      ? "Too many requests. Please wait a moment."
      : status >= 500
        ? "The Townsy service is temporarily unavailable."
        : `Request failed (${status}).`;
  return new ApiError(code, message, { status, requestId, retryable });
}

export async function apiFetch<T = unknown>(path: string, req: ApiRequest<T> = {}): Promise<T> {
  const url = path.startsWith("/") ? path : `${API_BASE}/${path}`;
  const headers: Record<string, string> = { accept: "application/json", ...(req.headers ?? {}) };
  const init: RequestInit = {
    method: req.method ?? "GET",
    credentials: "include",
    headers,
    signal: req.signal,
    keepalive: req.keepalive,
  };
  if (req.cache) init.cache = req.cache;
  if (req.body !== undefined) {
    headers["content-type"] = "application/json";
    init.body = JSON.stringify(req.body);
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError("DEPENDENCY_UNAVAILABLE", "Can't reach Townsy right now. Check your connection.", {
      status: 0,
      retryable: true,
    });
  }

  // Countdowns (checkout holds, quote expiry) follow the server's clock, not the visitor's.
  observeServerDate(res.headers?.get?.("date"));
  if (!res.ok) {
    const error = await readErrorBody(res);
    if (error.code === "STEP_UP_REQUIRED" && req.stepUp !== false) {
      const details = StepUpRequiredDetails.safeParse(error.details);
      // The server records the confirmation; the retry (same method, body and Idempotency-Key) is sent once.
      if (await requestStepUp(details.success ? details.data : null)) return apiFetch(path, { ...req, stepUp: false });
    }
    throw error;
  }
  if (res.status === 204) return undefined as T;

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new ApiError("INTERNAL", "The server returned an unreadable response.", { status: res.status });
  }
  if (!req.schema) return json as T;
  const parsed = req.schema.safeParse(json);
  if (!parsed.success) {
    console.warn(`[api] response for ${url} did not match the contract`, parsed.error.issues);
    throw new ApiError("INTERNAL", "The server returned an unexpected response.", {
      status: res.status,
      details: parsed.error.issues,
    });
  }
  return parsed.data;
}
